import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, CheckCircle2, CreditCard, GraduationCap, Target, CalendarPlus, Coins, XCircle, MessageSquare, Clock, Pencil, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../../api/notifications';
import { useAuth } from '../../context/AuthContext';
import { fermetureFond } from '../../utils/fermetureFond';

function timeAgo(date) {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}j`;
  return d.toLocaleDateString('fr-FR', { timeZone: 'Pacific/Tahiti', day: '2-digit', month: '2-digit' });
}

const ICONS = {
  payment_received: { Icon: CreditCard, cls: 'text-emerald-400' },
  rdv_present: { Icon: GraduationCap, cls: 'text-blue-400' },
  rdv_absent: { Icon: XCircle, cls: 'text-red-400' },
  rdv_retard: { Icon: Clock, cls: 'text-amber-400' },
  rdv_created: { Icon: CalendarPlus, cls: 'text-cyan-400' },
  prime_request: { Icon: Coins, cls: 'text-amber-400' },
  prime_approved: { Icon: CheckCircle2, cls: 'text-emerald-400' },
  prime_rejected: { Icon: XCircle, cls: 'text-red-400' },
  prime_edited: { Icon: Pencil, cls: 'text-blue-400' },
  closing_note: { Icon: MessageSquare, cls: 'text-purple-400' },
  closing_validated: { Icon: CheckCircle2, cls: 'text-emerald-400' },
};

export default function NotificationBell({ collapsed, variant = 'sidebar' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const btnRef = useRef(null);

  const isConnected = !!user;

  const load = async () => {
    try {
      const res = await getNotifications();
      setItems(res.data.items || []);
      setUnread(res.data.unread || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!isConnected) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isConnected]);

  useEffect(() => {
    const onClick = (e) => {
      const inDropdown = ref.current && ref.current.contains(e.target);
      const inButton = btnRef.current && btnRef.current.contains(e.target);
      if (!inDropdown && !inButton) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (!isConnected) return null;

  const handleClick = async (n) => {
    if (!n.read_at) {
      try { await markNotificationRead(n.id); } catch {}
      setItems(prev => prev.map(i => i.id === n.id ? { ...i, read_at: new Date().toISOString() } : i));
      setUnread(u => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const handleReadAll = async () => {
    try { await markAllNotificationsRead(); } catch {}
    setItems(prev => prev.map(i => ({ ...i, read_at: i.read_at || new Date().toISOString() })));
    setUnread(0);
  };

  // Le contenu de la liste est partagé entre le popover desktop et la feuille mobile
  const renderList = () => (
    <>
      <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Notifications</h3>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button onClick={handleReadAll} className="text-[10px] text-slate-400 hover:text-white flex items-center gap-1">
              <CheckCircle2 size={11} /> Tout marquer lu
            </button>
          )}
          {variant === 'mobile' && (
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white p-1"><X size={16} /></button>
          )}
        </div>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: variant === 'mobile' ? 'calc(85vh - 60px)' : '24rem' }}>
        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            <Bell size={24} className="mx-auto mb-2 opacity-50" />
            <p>Aucune notification</p>
          </div>
        ) : items.map(n => {
          const { Icon, cls } = ICONS[n.type] || { Icon: Target, cls: 'text-slate-400' };
          return (
            <button key={n.id} onClick={() => handleClick(n)}
              className={`w-full text-left px-4 py-3 border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors ${!n.read_at ? 'bg-purple-500/5' : ''}`}>
              <div className="flex items-start gap-3">
                <Icon size={16} className={`${cls} mt-0.5 shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${!n.read_at ? 'text-white' : 'text-slate-300'}`}>{n.title}</p>
                  {n.body && <p className="text-[11px] text-slate-400 mt-0.5 break-words">{n.body}</p>}
                  <div className="flex items-center gap-2 mt-1">
                    {n.actor_name && <span className="text-[10px] text-slate-500">par {n.actor_name}</span>}
                    <span className="text-[10px] text-slate-500">{timeAgo(n.created_at)}</span>
                  </div>
                </div>
                {!n.read_at && <div className="w-2 h-2 rounded-full bg-purple-500 shrink-0 mt-1" />}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );

  // ─── Variant MOBILE : icône en barre de nav + bottom-sheet pleine largeur ───
  if (variant === 'mobile') {
    return (
      <>
        <button ref={btnRef} onClick={() => setOpen(o => !o)}
          className="relative flex flex-col items-center justify-center gap-0.5 py-2 text-slate-400 active:bg-slate-800"
          aria-label="Notifications">
          <Bell size={22} />
          <span className="text-[10px]">Notifs</span>
          {unread > 0 && (
            <span className="absolute top-1 right-1/4 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>

        {open && createPortal(
          <div className="fixed inset-0 z-[99998] bg-black/60" {...fermetureFond(() => setOpen(false))}>
            <div ref={ref} onClick={e => e.stopPropagation()}
              className="absolute left-0 right-0 bottom-0 bg-slate-800 border-t border-slate-700 rounded-t-2xl shadow-2xl flex flex-col"
              style={{ maxHeight: '85vh' }}>
              <div className="w-12 h-1 bg-slate-600 rounded-full mx-auto mt-2 mb-1" />
              {renderList()}
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // ─── Variant TOPBAR (desktop, en-tête à droite) ───
  // Même liste que la sidebar, mais bouton icône seule et panneau ancré en
  // haut à droite sous l'en-tête (h-16 = top-16) au lieu de bas à gauche.
  if (variant === 'topbar') {
    return (
      <div className="relative">
        <button ref={btnRef} onClick={() => setOpen(!open)}
          className="relative text-on-surface-variant hover:text-primary transition-colors flex items-center p-1"
          title="Notifications">
          <Bell size={20} />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>

        {open && createPortal(
          <div ref={ref}
            className="fixed top-16 right-3 sm:right-5 w-96 max-w-[92vw] border border-slate-700 rounded-xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: 'rgb(30, 41, 59)', zIndex: 99999 }}>
            {renderList()}
          </div>,
          document.body
        )}
      </div>
    );
  }

  // ─── Variant SIDEBAR (desktop, bas de la sidebar) ───
  return (
    <div className="relative">
      <button ref={btnRef} onClick={() => setOpen(!open)}
        className={`relative w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors`}
        title="Notifications">
        <Bell size={18} />
        {!collapsed && <span className="text-sm">Notifications</span>}
        {unread > 0 && (
          <span className={`${collapsed ? 'absolute top-1 right-1' : 'ml-auto'} bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1`}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div ref={ref}
          className={`fixed ${collapsed ? 'left-20' : 'left-64'} bottom-20 w-96 max-w-[90vw] border border-slate-700 rounded-xl shadow-2xl overflow-hidden`}
          style={{ backgroundColor: 'rgb(30, 41, 59)', zIndex: 99999 }}>
          {renderList()}
        </div>,
        document.body
      )}
    </div>
  );
}
